import React, { useEffect, useState } from 'react';
import { bffService, FlexRow } from '@thisisdevelopment/akzonobel-core';
import { InspirationCard, Page } from '@thisisdevelopment/heritage-core-library';
import { GroupButton } from '@/pages/Colors/styled';
import { Articles } from '@/pages/Inspiration/styled';
import theme from '@/theme';
import { InspirationPageType, InspirationType } from '@/pages/Inspiration/types';
import NextHead from '@/components/NextHead';
import metas from '@/routes/meta';
import { GetStaticProps } from 'next';
import * as _ from 'lodash';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { pageRevalidate } from '../../../../src/config';

type ArticleGroup = {
  subCategory: string;
  highlightedArticle: InspirationType | null;
  articles: InspirationType[];
};

type SSRProps = {
  page: InspirationPageType;
  groups: ArticleGroup[];
  meta: any;
};

type GroupArticlesPropType = {
  selected: boolean;
};

export const GroupArticles = styled.div<GroupArticlesPropType>`
  display: ${({ selected }) => (selected ? 'initial' : 'none')};
  @media ${theme.mediaQuery.mobile} {
    display: initial;
  }
`;

export const FlexRowNoMobile = styled(FlexRow)`
  @media ${theme.mediaQuery.mobile} {
    display: none;
  }
`;

function Inspiration({ page, groups, meta }: SSRProps) {
  const [selectedGroup, setSelectedGroup] = useState<string>(groups[0].subCategory);
  const router = useRouter();

  useEffect(() => {
    /*
     * This fixes the issue where you navigate to a different category where the selected subCategory does not
     * exists. Not sure why the state is preserved in that case, but this resets the selects subCategory to it's
     * default in that case.
     */
    if (!groups.find((g) => g.subCategory === selectedGroup)) {
      setSelectedGroup(groups[0].subCategory);
    }
  }, [router.asPath]);

  return (
    <div>
      <NextHead meta={meta} />
      <Page title={page.title} headline={page.headline}>
        <FlexRowNoMobile padding="20px" justifyContent="center" alignItems="center">
          {groups.map((group) => (
            <GroupButton
              key={`GroupButton_${group.subCategory}`}
              onClick={() => setSelectedGroup(group.subCategory)}
              isActive={group.subCategory === selectedGroup}
            >
              {group.subCategory}
            </GroupButton>
          ))}
        </FlexRowNoMobile>
        {groups.map(({ subCategory, highlightedArticle, articles }) => (
          <GroupArticles
            selected={subCategory === selectedGroup}
            key={`GroupArticles_${subCategory}`}
          >
            {highlightedArticle && <InspirationCard highlight article={highlightedArticle} />}
            <Articles>
              {articles.map((article) => (
                <InspirationCard key={`InspirationCard_${article.id}`} article={article} />
              ))}
            </Articles>
          </GroupArticles>
        ))}
      </Page>
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const page = await bffService
    .getCmsItemBySlug('inspiration_pages', params?.category as string)
    .then((r) => r.data.data);
  const allArticles = await bffService.getCmsItem('inspiration').then((r) => r.data.data);
  const articles = allArticles.filter((p: InspirationType) =>
    p.category.includes(params?.category as string),
  ) as InspirationType[];

  const groups = [] as ArticleGroup[];
  _.map(_.groupBy(articles, 'sub_category'), (articles, key) => {
    groups.push({
      subCategory: key,
      highlightedArticle: articles.find(({ highlight }) => highlight) ?? null,
      articles: articles.filter(({ highlight }) => !highlight),
    });
  });

  return {
    props: {
      page,
      meta: metas.inspiration,
      groups,
    },
    revalidate: pageRevalidate,
  };
};

export async function getStaticPaths() {
  const pages = await bffService.getCmsItem('inspiration_pages').then((r) => r.data.data);

  const paths = pages.map((p: InspirationPageType) => ({
    params: { category: p.slug },
  }));

  return {
    paths,
    fallback: false,
  };
}

export default Inspiration;
